import { useEffect, useMemo, useState, useCallback } from "react"
import styled from "styled-components"
import { toast } from "react-hot-toast"
import { format } from "date-fns"
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  CalendarDays,
  FileText,
} from "lucide-react"
import { confirmDialog } from "../../utils/confirmDialog"

const REPORT_TYPES = [
  { value: "daily", label: "일일" },
  { value: "weekly", label: "주간" },
  { value: "monthly", label: "월간" },
]

const Wrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
  gap: 12px;
`

const Segment = styled.div`
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
`

const SegmentButton = styled.button`
  padding: 6px 14px;
  border: none;
  background: ${(p) => (p.active ? "white" : "transparent")};
  color: ${(p) => (p.active ? "#0f172a" : "#64748b")};
  font-size: 13px;
  font-weight: ${(p) => (p.active ? 600 : 500)};
  border-radius: 6px;
  cursor: pointer;
  box-shadow: ${(p) => (p.active ? "0 1px 2px rgba(0,0,0,0.05)" : "none")};
  transition: background 0.15s, color 0.15s;

  &:hover {
    color: #0f172a;
  }
`

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #2563eb;
  color: white;
  border: 1px solid #2563eb;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) { background: #1d4ed8; }

  &:disabled {
    background: #cbd5e1;
    border-color: #cbd5e1;
    cursor: not-allowed;
  }
`

const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  padding: 16px 18px;
  overflow: hidden;
  min-height: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
`

const Card = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const CalendarCard = styled(Card)`
  padding: 12px;
`

const CalendarHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
  }
`

const CalendarNavButton = styled.button`
  width: 26px;
  height: 26px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #475569;

  &:hover { background: #f1f5f9; }
`

const WeekHead = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 4px;

  div {
    text-align: center;
    padding: 4px 0;
  }
`

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`

const DayCell = styled.button`
  position: relative;
  aspect-ratio: 1;
  border: 1px solid ${(p) => (p.selected ? "#2563eb" : "transparent")};
  background: ${(p) =>
    p.selected ? "#eff6ff" : p.inRange ? "#f8fafc" : "transparent"};
  color: ${(p) =>
    p.outOfMonth ? "#cbd5e1" : p.today ? "#2563eb" : "#0f172a"};
  font-size: 12px;
  font-weight: ${(p) => (p.today ? 700 : 500)};
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.1s;

  &:hover { background: ${(p) => (p.selected ? "#dbeafe" : "#f1f5f9")}; }
`

const DotRow = styled.span`
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  gap: 2px;
  pointer-events: none;
`

const DayDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => p.color || "#2563eb"};
  box-shadow: 0 0 0 1.5px white;
`

const TYPE_DOT_COLOR = {
  daily: "#f59e0b",
  weekly: "#3b82f6",
  monthly: "#a855f7",
}

const TYPE_BADGE_BG = {
  daily: "#fef3c7",
  weekly: "#dbeafe",
  monthly: "#f3e8ff",
}

const TYPE_BADGE_TEXT = {
  daily: "#92400e",
  weekly: "#1e40af",
  monthly: "#6b21a8",
}

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${(p) => TYPE_BADGE_BG[p.type] || "#f1f5f9"};
  color: ${(p) => TYPE_BADGE_TEXT[p.type] || "#475569"};
  border: 1px solid ${(p) => TYPE_DOT_COLOR[p.type] || "#cbd5e1"}40;
  line-height: 1;

  &::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => TYPE_DOT_COLOR[p.type] || "#94a3b8"};
  }
`

// 헤더 색조: 타입별로 옅은 배경 띠
const HeaderTypeAccent = styled.div`
  height: 4px;
  background: ${(p) => TYPE_DOT_COLOR[p.type] || "#cbd5e1"};
  width: 100%;
`

const ListCard = styled(Card)`
  flex: 1;
  min-height: 0;
`

const ListHeader = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ListBody = styled.div`
  flex: 1;
  overflow-y: auto;
`

const ListItem = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  background: ${(p) => (p.active ? "#eff6ff" : "transparent")};
  border: none;
  border-left: 3px solid ${(p) => (p.active ? "#2563eb" : "transparent")};
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid #f8fafc;

  &:hover { background: ${(p) => (p.active ? "#eff6ff" : "#f8fafc")}; }
`

const ListTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ListMeta = styled.div`
  font-size: 11px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 6px;
`

const Empty = styled.div`
  padding: 24px 14px;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
`

const DetailPanel = styled(Card)`
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const DetailHeader = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
`

const DetailTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    font-size: 12px;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
`

const DetailActions = styled.div`
  display: inline-flex;
  gap: 6px;
  flex-shrink: 0;
`

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  font-size: 12px;
  border: 1px solid ${(p) => p.color || "#e2e8f0"};
  background: white;
  color: ${(p) => p.color || "#475569"};
  border-radius: 6px;
  cursor: pointer;

  &:hover { background: ${(p) => (p.color ? `${p.color}10` : "#f8fafc")}; }
`

const DetailBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px;
`

const Section = styled.section`
  & + & { margin-top: 18px; }
`

const SectionLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
`

const ReadOnlyText = styled.div`
  font-size: 13px;
  color: #0f172a;
  line-height: 1.6;
  white-space: pre-wrap;
  min-height: 20px;
`

const TextInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  color: #0f172a;
  background: white;
  box-sizing: border-box;

  &:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 1px #2563eb; }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  color: #0f172a;
  background: white;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 1px #2563eb; }
`

const DateRangeRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const TaskChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const TaskChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  background: ${(p) => (p.selected ? "#dbeafe" : "#f1f5f9")};
  border: 1px solid ${(p) => (p.selected ? "#2563eb" : "transparent")};
  color: ${(p) => (p.selected ? "#1e3a8a" : "#475569")};
  border-radius: 999px;
  cursor: pointer;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
  gap: 10px;
  padding: 40px 20px;
`

const TaskEntryView = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 8px;
  background: #f9fafb;
`

const TaskEntryEdit = styled.div`
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 8px;
  background: white;
`

const TaskEntryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`

const TaskEntryName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TaskEntryMeta = styled.span`
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
`

const TaskEntryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const TaskEntrySubLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 3px;
`

// ── 날짜 유틸 ──
const toDate = (v) => {
  if (!v) return null
  if (v instanceof Date) return v
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}
const ymd = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}
const startOfWeek = (d) => {
  // Monday 시작
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  const r = new Date(d)
  r.setDate(d.getDate() + diff)
  r.setHours(0, 0, 0, 0)
  return r
}
const endOfWeek = (d) => {
  const s = startOfWeek(d)
  const e = new Date(s)
  e.setDate(s.getDate() + 6)
  return e
}
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)
const sameYMD = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

const computePeriod = (type, dateObj) => {
  if (type === "daily") return { start: ymd(dateObj), end: ymd(dateObj) }
  if (type === "weekly") return { start: ymd(startOfWeek(dateObj)), end: ymd(endOfWeek(dateObj)) }
  return { start: ymd(startOfMonth(dateObj)), end: ymd(endOfMonth(dateObj)) }
}

const formatPeriod = (type, start, end) => {
  if (!start) return ""
  if (type === "daily") return start
  return `${start} ~ ${end}`
}

// 사람이 읽기 좋은 기간 라벨 (자동 제목용)
const formatPeriodLabel = (type, dateObj) => {
  if (!dateObj) return ""
  const y = dateObj.getFullYear()
  const m = dateObj.getMonth() + 1
  const d = dateObj.getDate()
  if (type === "daily") return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  if (type === "weekly") {
    const ws = startOfWeek(dateObj)
    const weekNo = Math.ceil(ws.getDate() / 7)
    return `${ws.getFullYear()}년 ${ws.getMonth() + 1}월 ${weekNo}주차`
  }
  return `${y}년 ${m}월`
}

const formatPeriodLabelFromStr = (type, startStr) => {
  if (!startStr) return ""
  return formatPeriodLabel(type, new Date(startStr))
}

// 작업 한 개를 ID로 찾기
const findTask = (tasks, id) => tasks.find((t) => String(t.id) === String(id))

const normalizeYmd = (v) => {
  if (!v) return ""
  if (typeof v === "string") return v.slice(0, 10)
  return ymd(v)
}

const isDateInRange = (target, start, end) => {
  const t = normalizeYmd(target)
  const s = normalizeYmd(start)
  const e = normalizeYmd(end)
  return t >= s && t <= e
}

// 캘린더용 month grid
function getMonthGrid(viewMonth) {
  const first = startOfMonth(viewMonth)
  const last = endOfMonth(viewMonth)
  const gridStart = startOfWeek(first)
  const days = []
  let d = new Date(gridStart)
  for (let i = 0; i < 42; i++) {
    days.push(new Date(d))
    d.setDate(d.getDate() + 1)
  }
  return { days, firstMonth: first.getMonth(), last }
}

// 기간에 겹치는 작업 자동 매칭 (start <= period_end && end >= period_start)
const tasksOverlappingPeriod = (allTasks, periodStartStr, periodEndStr) => {
  if (!periodStartStr || !periodEndStr) return []
  return allTasks.filter((t) => {
    const s = toDate(t.startDate)
    const e = toDate(t.endDate)
    if (!s || !e) return false
    return ymd(s) <= periodEndStr && ymd(e) >= periodStartStr
  })
}

export default function WorkReportPanel({ chartId, tasks = [], users = [], currentUserId, jumpTask, onJumpHandled }) {
  const [reportType, setReportType] = useState("weekly")
  const [loading, setLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [viewMonth, setViewMonth] = useState(startOfMonth(new Date()))
  const [selectedId, setSelectedId] = useState(null)
  const [mode, setMode] = useState("view") // 'view' | 'edit' | 'create'
  // 단순 모델: 1 보고서 = 1 작업 + 1 기간 + 진행사항/이슈
  const [draft, setDraft] = useState({
    taskId: null,
    period_start: "",
    period_end: "",
    progress: "",
    issue: "",
  })

  // 모든 보고서 (전체 차트, 타입 무관) - 캘린더 점 표시 + 날짜 매칭용
  const [allReports, setAllReports] = useState([])

  // 현재 타입 필터 적용된 리스트 (좌측 목록 표시용)
  const reports = useMemo(
    () => allReports.filter((r) => r.report_type === reportType),
    [allReports, reportType]
  )

  // 보고서 불러오기 (타입 필터 없이 전체)
  const loadReports = useCallback(async () => {
    if (!chartId) return
    setLoading(true)
    try {
      const res = await fetch(`/request/work-reports?chart_id=${chartId}`, {
        credentials: "include",
      })
      const data = await res.json()
      if (data.success) setAllReports(data.reports || [])
      else toast.error(data.message || "보고서 목록 조회 실패")
    } catch (e) {
      console.error(e)
      toast.error("서버 연결 오류")
    } finally {
      setLoading(false)
    }
  }, [chartId])

  useEffect(() => { loadReports() }, [loadReports])

  // 선택된 보고서 (allReports 기준 — 타입과 무관하게 ID로 찾음)
  const selectedReport = useMemo(
    () => allReports.find((r) => r.id === selectedId) || null,
    [allReports, selectedId]
  )

  // 선택 날짜 변경 시: 현재 탭에 해당하는 보고서로 매칭. 단 사용자가 직접 선택한 보고서가
  // 여전히 이 날짜/타입 범위 안에 있으면 그대로 유지 (같은 날 여러 보고서 중 선택 보존)
  useEffect(() => {
    if (!selectedDate || allReports.length === 0) {
      setSelectedId(null)
      return
    }
    const current = allReports.find((r) => r.id === selectedId)
    if (
      current &&
      current.report_type === reportType &&
      isDateInRange(selectedDate, current.period_start, current.period_end)
    ) {
      return
    }
    const match = allReports.find(
      (r) => r.report_type === reportType && isDateInRange(selectedDate, r.period_start, r.period_end)
    )
    setSelectedId(match ? match.id : null)
    setMode("view")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, allReports])

  // 탭(타입) 변경 시: 같은 날짜에 그 타입 보고서가 있으면 자동 선택, 없으면 해제
  useEffect(() => {
    if (!selectedDate || allReports.length === 0) {
      setSelectedId(null)
      return
    }
    const match = allReports.find(
      (r) => r.report_type === reportType && isDateInRange(selectedDate, r.period_start, r.period_end)
    )
    setSelectedId(match ? match.id : null)
    setMode("view")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType])

  // 캘린더에 점 찍기 위해 보고서 날짜 집합 만들기 (전체 타입 통합)
  const reportDateSet = useMemo(() => {
    const s = new Set()
    allReports.forEach((r) => {
      const start = toDate(r.period_start)
      const end = toDate(r.period_end)
      if (!start || !end) return
      const d = new Date(start)
      while (d <= end) {
        s.add(ymd(d))
        d.setDate(d.getDate() + 1)
      }
    })
    return s
  }, [reports])

  const grid = useMemo(() => getMonthGrid(viewMonth), [viewMonth])

  // 특정 작업 + 현재 기간으로 작성/수정 진입 (기존 보고서 있으면 수정 모드)
  const openForTask = useCallback((taskId, dateObj) => {
    const task = findTask(tasks, taskId)
    if (!task) {
      toast.error("작업을 찾을 수 없습니다")
      return
    }
    const target = dateObj || selectedDate
    const { start, end } = computePeriod(reportType, target)

    // 동일 작업+동일 기간 보고서가 있으면 수정 모드로
    const existing = reports.find(
      (r) =>
        Array.isArray(r.linked_task_ids) &&
        r.linked_task_ids.length === 1 &&
        String(r.linked_task_ids[0]) === String(taskId) &&
        r.period_start.slice(0, 10) === start &&
        r.period_end.slice(0, 10) === end
    )

    if (existing) {
      setSelectedId(existing.id)
      const c = existing.content || {}
      setDraft({
        taskId: String(taskId),
        period_start: start,
        period_end: end,
        progress: c.progress || c.summary || "",
        issue: c.issue || c.issues || "",
      })
      setMode("edit")
    } else {
      setDraft({
        taskId: String(taskId),
        period_start: start,
        period_end: end,
        progress: "",
        issue: "",
      })
      setMode("create")
      setSelectedId(null)
    }
    setSelectedDate(target)
    setViewMonth(startOfMonth(target))
  }, [tasks, reports, reportType, selectedDate])

  // WBS에서 작업 클릭으로 점프해 온 경우: 해당 작업+기간의 보고서 자동 열기
  useEffect(() => {
    if (!jumpTask || !jumpTask.taskId) return
    const date = jumpTask.date instanceof Date ? jumpTask.date : new Date(jumpTask.date)
    if (isNaN(date.getTime())) return
    openForTask(jumpTask.taskId, date)
    onJumpHandled?.()
  }, [jumpTask, openForTask, onJumpHandled])

  // 보고서 카드 → 수정 진입
  const startEdit = () => {
    if (!selectedReport) return
    const c = selectedReport.content || {}
    const tid = (selectedReport.linked_task_ids && selectedReport.linked_task_ids[0]) || null
    setDraft({
      taskId: tid ? String(tid) : null,
      period_start: selectedReport.period_start.slice(0, 10),
      period_end: selectedReport.period_end.slice(0, 10),
      progress: c.progress || c.summary || "",
      issue: c.issue || c.issues || "",
    })
    setMode("edit")
  }

  // 저장
  const save = async () => {
    if (!draft.taskId) {
      toast.error("작업이 지정되지 않았습니다")
      return
    }
    if (!draft.progress.trim()) {
      toast.error("진행사항을 입력해주세요")
      return
    }
    const task = findTask(tasks, draft.taskId)
    const autoTitle = task
      ? `${task.name} · ${formatPeriodLabelFromStr(reportType, draft.period_start)}`
      : null

    const payload = {
      chart_id: chartId,
      report_type: reportType,
      period_start: draft.period_start,
      period_end: draft.period_end,
      title: autoTitle,
      content: { progress: draft.progress, issue: draft.issue },
      linked_task_ids: [String(draft.taskId)],
    }

    try {
      if (mode === "create") {
        const res = await fetch("/request/work-report", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        toast.success("저장되었습니다")
        await loadReports()
        setSelectedId(data.id)
        setMode("view")
      } else if (mode === "edit" && selectedReport) {
        const res = await fetch(`/request/work-report/${selectedReport.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            title: payload.title,
            content: payload.content,
            period_start: payload.period_start,
            period_end: payload.period_end,
            linked_task_ids: payload.linked_task_ids,
          }),
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        toast.success("수정되었습니다")
        await loadReports()
        setMode("view")
      }
    } catch (e) {
      console.error(e)
      toast.error(e.message || "저장 실패")
    }
  }

  // 삭제
  const remove = async () => {
    if (!selectedReport) return
    if (!(await confirmDialog("이 보고서를 삭제할까요?"))) return
    try {
      const res = await fetch(`/request/work-report/${selectedReport.id}`, {
        method: "DELETE",
        credentials: "include",
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.message)
      toast.success("삭제되었습니다")
      setSelectedId(null)
      setMode("view")
      await loadReports()
    } catch (e) {
      console.error(e)
      toast.error(e.message || "삭제 실패")
    }
  }

  const getTaskById = (id) => tasks.find((t) => String(t.id) === String(id))

  const getAuthorName = (uid) => {
    const u = users.find((x) => x.id === uid || x.user_id === uid)
    return u?.name || u?.user_name || uid?.slice(0, 8) || "?"
  }

  const today = new Date()

  return (
    <Wrapper>
      <Toolbar>
        <Segment>
          {REPORT_TYPES.map((t) => (
            <SegmentButton
              key={t.value}
              active={reportType === t.value}
              onClick={() => {
                setReportType(t.value)
                setMode("view")
                setSelectedId(null)
              }}
            >
              {t.label}
            </SegmentButton>
          ))}
        </Segment>

        <span style={{ fontSize: 12, color: "#94a3b8" }}>
          WBS 탭의 작업을 클릭하면 작성/수정할 수 있어요
        </span>
      </Toolbar>

      <Body>
        <SidePanel>
          <CalendarCard>
            <CalendarHead>
              <CalendarNavButton onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>
                <ChevronLeft size={14} />
              </CalendarNavButton>
              <h3>
                {viewMonth.getFullYear()}년 {viewMonth.getMonth() + 1}월
              </h3>
              <CalendarNavButton onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>
                <ChevronRight size={14} />
              </CalendarNavButton>
            </CalendarHead>
            <WeekHead>
              {["월", "화", "수", "목", "금", "토", "일"].map((w) => (
                <div key={w}>{w}</div>
              ))}
            </WeekHead>
            <DayGrid>
              {grid.days.map((d, i) => {
                const isOut = d.getMonth() !== grid.firstMonth
                const isToday = sameYMD(d, today)
                const isSelected = sameYMD(d, selectedDate)
                const dayYmd = ymd(d)
                // 현재 탭(reportType)의 보고서만 점으로 표시
                const dayReports = allReports.filter(
                  (r) =>
                    r.report_type === reportType &&
                    dayYmd >= r.period_start.slice(0, 10) &&
                    dayYmd <= r.period_end.slice(0, 10)
                )
                const hasReport = dayReports.length > 0
                const typeLabel = REPORT_TYPES.find((rt) => rt.value === reportType)?.label
                return (
                  <DayCell
                    key={i}
                    outOfMonth={isOut}
                    today={isToday}
                    selected={isSelected}
                    onClick={() => setSelectedDate(new Date(d))}
                    title={hasReport ? `${typeLabel} 보고서 ${dayReports.length}건` : undefined}
                  >
                    {d.getDate()}
                    {hasReport && (
                      <DotRow>
                        <DayDot color={TYPE_DOT_COLOR[reportType]} />
                      </DotRow>
                    )}
                  </DayCell>
                )
              })}
            </DayGrid>
          </CalendarCard>

          <ListCard>
            <ListHeader>
              <span>
                {reports.length}건 · {REPORT_TYPES.find((t) => t.value === reportType)?.label}
              </span>
              {loading && <span style={{ color: "#94a3b8", fontWeight: 400 }}>불러오는 중…</span>}
            </ListHeader>
            <ListBody>
              {reports.length === 0 && !loading ? (
                <Empty>작성된 보고서가 없습니다.<br />WBS 작업을 클릭하면 작성할 수 있어요.</Empty>
              ) : (
                reports.map((r) => {
                  const tid = (r.linked_task_ids && r.linked_task_ids[0]) || null
                  const t = tid ? findTask(tasks, tid) : null
                  const periodLabel = formatPeriodLabelFromStr(r.report_type, r.period_start.slice(0, 10))
                  return (
                    <ListItem
                      key={r.id}
                      active={r.id === selectedId}
                      onClick={() => {
                        setSelectedId(r.id)
                        setMode("view")
                        const d = toDate(r.period_start)
                        if (d) {
                          setSelectedDate(d)
                          setViewMonth(startOfMonth(d))
                        }
                      }}
                    >
                      <ListTitle>{t?.name || "(작업 없음)"}</ListTitle>
                      <ListMeta>
                        <CalendarDays size={11} />
                        {periodLabel}
                      </ListMeta>
                    </ListItem>
                  )
                })
              )}
            </ListBody>
          </ListCard>
        </SidePanel>

        <DetailPanel>
          {mode === "view" && !selectedReport && (() => {
            // 선택된 날짜에 해당하는 기간을 계산하고, 그 기간에 진행 중인 작업 목록 추출
            const periodForType = computePeriod(reportType, selectedDate)
            const candidateTasks = tasks.filter((t) => {
              const s = toDate(t.startDate)
              const e = toDate(t.endDate)
              if (!s || !e) return false
              return ymd(s) <= periodForType.end && ymd(e) >= periodForType.start
            })
            const typeLabel = REPORT_TYPES.find((rt) => rt.value === reportType)?.label
            const periodDisplayLabel = formatPeriodLabel(reportType, selectedDate)
            return (
              <>
                <DetailHeader>
                  <DetailTitleBlock>
                    <h2>{periodDisplayLabel}</h2>
                    <span>
                      <CalendarDays size={12} />
                      {typeLabel} 보고서 · 작성 가능한 작업 {candidateTasks.length}개
                    </span>
                  </DetailTitleBlock>
                </DetailHeader>
                <DetailBody>
                  {candidateTasks.length === 0 ? (
                    <EmptyState>
                      <FileText size={32} strokeWidth={1.5} color="#cbd5e1" />
                      <div>
                        이 기간에 진행 중인 작업이 없어요.<br />
                        다른 날짜를 선택하거나 WBS에서 작업을 추가해주세요.
                      </div>
                    </EmptyState>
                  ) : (
                    <Section>
                      <SectionLabel>이 {typeLabel}에 작성할 작업을 선택하세요</SectionLabel>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {candidateTasks.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => openForTask(t.id, selectedDate)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              padding: "10px 12px",
                              border: "1px solid #e2e8f0",
                              borderRadius: 8,
                              background: "white",
                              cursor: "pointer",
                              textAlign: "left",
                              transition: "border-color 0.15s, background 0.15s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = "#2563eb"
                              e.currentTarget.style.background = "#eff6ff"
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#e2e8f0"
                              e.currentTarget.style.background = "white"
                            }}
                          >
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {t.name}
                              </div>
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                {t.startDate && format(new Date(t.startDate), "MM.dd")}
                                {" ~ "}
                                {t.endDate && format(new Date(t.endDate), "MM.dd")}
                                {typeof t.progress === "number" && ` · ${t.progress}%`}
                              </div>
                            </div>
                            <Plus size={16} color="#2563eb" />
                          </button>
                        ))}
                      </div>
                    </Section>
                  )}
                </DetailBody>
              </>
            )
          })()}

          {mode === "view" && selectedReport && (() => {
            const tid = (selectedReport.linked_task_ids && selectedReport.linked_task_ids[0]) || null
            const t = tid ? findTask(tasks, tid) : null
            const c = selectedReport.content || {}
            const progress = c.progress || c.summary || ""
            const issue = c.issue || c.issues || ""
            const rType = selectedReport.report_type
            const rTypeLabel = REPORT_TYPES.find((rt) => rt.value === rType)?.label
            const ps = selectedReport.period_start.slice(0, 10)
            const pe = selectedReport.period_end.slice(0, 10)
            const fullRange = rType === "daily" ? ps : `${ps} ~ ${pe}`
            const periodLabel = formatPeriodLabelFromStr(rType, ps)
            const isOwn = selectedReport.user_id === currentUserId
            const authorDisplay = isOwn ? "본인" : getAuthorName(selectedReport.user_id)
            return (
              <>
                <HeaderTypeAccent type={rType} />
                <DetailHeader>
                  <DetailTitleBlock>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <TypeBadge type={rType}>{rTypeLabel} 보고서</TypeBadge>
                      <span style={{ fontSize: 12, color: "#64748b" }}>{periodLabel}</span>
                    </div>
                    <h2>{t?.name || "(작업 없음)"}</h2>
                    <span>
                      <CalendarDays size={12} />
                      {fullRange} · {authorDisplay}
                    </span>
                  </DetailTitleBlock>
                  <DetailActions>
                    {isOwn && (
                      <>
                        <IconBtn onClick={startEdit}>
                          <Pencil size={12} /> 수정
                        </IconBtn>
                        <IconBtn color="#dc2626" onClick={remove}>
                          <Trash2 size={12} /> 삭제
                        </IconBtn>
                      </>
                    )}
                  </DetailActions>
                </DetailHeader>
                <DetailBody>
                  <Section>
                    <SectionLabel>진행사항</SectionLabel>
                    <ReadOnlyText>{progress || "—"}</ReadOnlyText>
                  </Section>
                  {issue && (
                    <Section>
                      <SectionLabel>이슈</SectionLabel>
                      <ReadOnlyText>{issue}</ReadOnlyText>
                    </Section>
                  )}
                </DetailBody>
              </>
            )
          })()}

          {(mode === "create" || mode === "edit") && (() => {
            const t = draft.taskId ? findTask(tasks, draft.taskId) : null
            // 수정 모드: 원본 보고서의 타입 (현재 탭과 다를 수 있음)
            // 작성 모드: 현재 탭의 타입
            const draftType = mode === "edit" && selectedReport ? selectedReport.report_type : reportType
            const draftTypeLabel = REPORT_TYPES.find((rt) => rt.value === draftType)?.label
            const periodLabel = formatPeriodLabelFromStr(draftType, draft.period_start)
            const fullRange = draftType === "daily"
              ? draft.period_start
              : `${draft.period_start} ~ ${draft.period_end}`
            return (
              <>
                <HeaderTypeAccent type={draftType} />
                <DetailHeader>
                  <DetailTitleBlock>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <TypeBadge type={draftType}>
                        {mode === "edit" ? `${draftTypeLabel} 보고서 수정` : `${draftTypeLabel} 보고서 작성`}
                      </TypeBadge>
                      <span style={{ fontSize: 12, color: "#64748b" }}>{periodLabel}</span>
                    </div>
                    <h2>{t?.name || "(작업 없음)"}</h2>
                    <span>
                      <CalendarDays size={12} />
                      {fullRange}
                    </span>
                  </DetailTitleBlock>
                  <DetailActions>
                    <IconBtn color="#2563eb" onClick={save}>
                      <Save size={12} /> 저장
                    </IconBtn>
                    <IconBtn onClick={() => setMode("view")}>
                      <X size={12} /> 취소
                    </IconBtn>
                  </DetailActions>
                </DetailHeader>
                <DetailBody>
                  <Section>
                    <SectionLabel>진행사항</SectionLabel>
                    <TextArea
                      autoFocus
                      placeholder="이 작업에 대해 한 일을 적어주세요"
                      value={draft.progress}
                      onChange={(e) => setDraft({ ...draft, progress: e.target.value })}
                      style={{ minHeight: 140 }}
                    />
                  </Section>
                  <Section>
                    <SectionLabel>이슈 (선택)</SectionLabel>
                    <TextInput
                      placeholder="블로커, 지연 사유 등"
                      value={draft.issue}
                      onChange={(e) => setDraft({ ...draft, issue: e.target.value })}
                    />
                  </Section>
                </DetailBody>
              </>
            )
          })()}
        </DetailPanel>
      </Body>
    </Wrapper>
  )
}
